import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Filler, registerables } from 'chart.js';
import { formatChartRoundHourTime, formatChartAxisTimeSuppressDuplicates } from '../utils/timeUtils';
import { buildChartAreaGradient } from '../utils/chartAreaGradient';
import { useTheme, getWaterChartTheme } from '../context/ThemeContext';

ChartJS.register(...registerables, Filler);

const RANGE_OPTIONS = [
  { value: 6, label: '6h' },
  { value: 24, label: '24h' },
  { value: 72, label: '72h' },
];

function formatChartLabel(timestamp, locale = 'en-GB') {
  return formatChartRoundHourTime(timestamp, locale);
}

function buildChartScales(chartTheme, yTitle, t = (key) => key, xLabels = null) {
  const { slate, grid } = chartTheme;
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    elements: {
      line: {
        cubicInterpolationMode: 'monotone',
        tension: 0.4,
        borderJoinStyle: 'round',
        borderCapStyle: 'round',
      },
      point: {
        radius: 0,
        hoverRadius: 4,
        hitRadius: 12,
        borderWidth: 0,
      },
    },
    plugins: {
      legend: {
        labels: { color: slate, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        grid: { color: grid },
        ticks: {
          color: slate,
          maxRotation: 0,
          ...(xLabels && xLabels.length
            ? {
                callback(_, index) {
                  return formatChartAxisTimeSuppressDuplicates(xLabels, index);
                },
              }
            : {}),
        },
        title: { display: true, text: t('time'), color: slate },
        border: { display: false },
      },
      y: {
        grid: { color: grid },
        ticks: { color: slate },
        title: { display: true, text: yTitle, color: slate },
        border: { display: false },
      },
    },
  };
}

function lineDataset(label, data, borderColor) {
  return {
    label,
    data,
    borderColor,
    backgroundColor: (context) => buildChartAreaGradient(context, borderColor),
    cubicInterpolationMode: 'monotone',
    tension: 0.4,
    fill: true,
    borderWidth: 2,
    borderJoinStyle: 'round',
    borderCapStyle: 'round',
    pointRadius: 0,
    pointHoverRadius: 4,
    pointBorderWidth: 0,
    pointBackgroundColor: 'transparent',
  };
}

function getPhScaleBounds(data) {
  const values = data
    .map((item) => Number(item.ph))
    .filter((value) => Number.isFinite(value));

  if (!values.length) {
    return { min: 4.8, max: 7.2 };
  }

  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const min = Math.max(0, Math.floor((rawMin - 0.3) * 10) / 10);
  const max = Math.min(14, Math.ceil((rawMax + 0.3) * 10) / 10);

  if (max - min < 1) {
    const center = (max + min) / 2;
    return {
      min: Math.max(0, Number((center - 0.5).toFixed(1))),
      max: Math.min(14, Number((center + 0.5).toFixed(1))),
    };
  }

  return { min, max };
}

export default function WaterCharts({ deviceId, t = (key) => key }) {
  const { isLight } = useTheme();
  const chartTheme = useMemo(() => getWaterChartTheme(isLight), [isLight]);
  const [hours, setHours] = useState(24);
  const [data, setData] = useState([]);
  const rawWaterPts = Number(process.env.REACT_APP_WATER_CHART_MAX_POINTS) || 1100;
  const maxPoints = Math.min(1200, Math.max(1000, rawWaterPts));

  useEffect(() => {
    fetch(`/api/sensor-data/water/history/${deviceId}?hours=${hours}&maxPoints=${maxPoints}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((payload) => (Array.isArray(payload) ? payload : []))
      .then(setData)
      .catch((err) => console.error('Failed to fetch water history', err));
  }, [deviceId, hours, maxPoints]);

  const labels = useMemo(
    () => data.map((item) => formatChartLabel(item.timestamp || item.ts)),
    [data]
  );
  const phScale = useMemo(() => getPhScaleBounds(data), [data]);

  const { ph: cPh, ec: cEc, orp: cOrp, temp: cTemp, volume: cVol } = chartTheme.datasets;

  const chartData = useMemo(() => {
    if (!data.length) return null;
    return {
      ph: {
        labels,
        datasets: [lineDataset('pH', data.map((item) => item.ph ?? null), cPh.border)],
      },
      ec: {
        labels,
        datasets: [lineDataset('EC', data.map((item) => item.ec ?? null), cEc.border)],
      },
      orp: {
        labels,
        datasets: [lineDataset('ORP', data.map((item) => item.orp ?? null), cOrp.border)],
      },
      temp: {
        labels,
        datasets: [
          lineDataset(
            'Temp',
            data.map((item) => item.ecTemp ?? item.temp ?? item.temperature ?? null),
            cTemp.border
          ),
        ],
      },
      volume: {
        labels,
        datasets: [lineDataset('Volume', data.map((item) => item.volume ?? null), cVol.border)],
      },
    };
  }, [data, labels, cPh, cEc, cOrp, cTemp, cVol]);

  if (!data.length || !chartData) {
    return <div className="text-muted small">{t('noWaterHistory')}</div>;
  }

  const baseOpts = (yTitle) => buildChartScales(chartTheme, yTitle, t, labels);
  const rangeActive = 'btn shefa-water-range-btn shefa-water-range-btn--active';
  const rangeIdle = 'btn shefa-water-range-btn';

  return (
    <div className="water-status-card__charts">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h6 className="fw-semibold mb-0">{t('waterTrends')}</h6>
        <div className="btn-group btn-group-sm" role="group" aria-label={t('waterHistoryRange')}>
          {RANGE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={hours === option.value ? rangeActive : rangeIdle}
              onClick={() => setHours(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="water-status-card__chart-panel">
        <h6 className="fw-bold mb-2">pH</h6>
        <div className="water-chart-canvas-wrap">
          <Line
            data={chartData.ph}
            options={{
              ...baseOpts('pH'),
              scales: {
                ...baseOpts('pH').scales,
                y: {
                  min: phScale.min,
                  max: phScale.max,
                  grid: { color: chartTheme.grid },
                  ticks: { color: chartTheme.slate },
                  title: { display: true, text: 'pH', color: chartTheme.slate },
                  border: { display: false },
                },
              },
            }}
          />
        </div>
      </div>

      <div className="water-status-card__chart-panel">
        <h6 className="fw-bold mb-2">{t('ec') || 'EC'}</h6>
        <div className="water-chart-canvas-wrap">
          <Line data={chartData.ec} options={baseOpts('EC (mS/cm)')} />
        </div>
      </div>

      <div className="water-status-card__chart-panel">
        <h6 className="fw-bold mb-2">ORP</h6>
        <div className="water-chart-canvas-wrap">
          <Line data={chartData.orp} options={baseOpts('ORP (mV)')} />
        </div>
      </div>

      <div className="water-status-card__chart-panel">
        <h6 className="fw-bold mb-2">{t('waterTemperature')}</h6>
        <div className="water-chart-canvas-wrap">
          <Line data={chartData.temp} options={baseOpts('°C')} />
        </div>
      </div>

      <div className="water-status-card__chart-panel">
        <h6 className="fw-bold mb-2">{t('waterVolume')}</h6>
        <div className="water-chart-canvas-wrap">
          <Line data={chartData.volume} options={baseOpts(t('liters'))} />
        </div>
      </div>
    </div>
  );
}
